import React from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'
import canUseDom from '@boltui/utils/src/canUseDom'
import Alert from '@boltui/alert'

class ErrorBoundary extends React.Component {
  static defaultProps = {
    retries: 0,
    showErrorMsg: false,
    errorMsg: "Oops, something's gone wrong. We're working hard to fix it... please try again later.",
    alertProps: {
      kind: 'error',
      isCloseable: false
    },
    callbackFunction: () => {},
    useSentry: true
  }

  static propTypes = {
    /**
     * Customize the alert message if needed
     */
    alertProps: PropTypes.shape({
      isCloseable: PropTypes.bool,
      kind: PropTypes.string
    }),
    /**
     * If error is caught, fire a custom function
     */
    callbackFunction: PropTypes.func,
    /**
     * Specify the content of your ErrorBoundary
     */
    children: PropTypes.node,
    /**
     * If showErrorMsg, then you can define a custom error message
     */
    errorMsg: PropTypes.string,
    /**
     * If error is caught, display a custom component
     */
    FallbackComponent: PropTypes.element,
    /**
     * Set the amount of times to retry a success state until error is captured
     */
    retries: PropTypes.number,
    /**
     * If error is caught, to show or not to show the error message
     */
    showErrorMsg: PropTypes.bool,
    /**
     * If error is caught, log it with default sentry config
     */
    useSentry: PropTypes.bool
  }

  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      errorCount: 0
    }
  }

  componentDidCatch(error, errorInfo) {
    const { retries, callbackFunction, useSentry } = this.props
    if (retries >= 1) {
      this.setState((oldState) => ({ errorCount: oldState.errorCount + 1 }))
    } else {
      this.setState({
        hasError: true
      })
      if (useSentry && canUseDom()) {
        Sentry.withScope((scope) => {
          Object.keys(errorInfo).forEach((key) => {
            scope.setExtra(key, errorInfo[key])
          })
          Sentry.captureException(error)
        })
      }
      if (typeof callbackFunction === 'function') callbackFunction()
    }
  }

  render() {
    const { children, retries, showErrorMsg, errorMsg, FallbackComponent, alertProps } = this.props
    const { errorCount, hasError } = this.state

    if ((retries >= 1 && errorCount > retries) || hasError) {
      if (showErrorMsg) return <Alert {...alertProps}>{errorMsg}</Alert>
      if (FallbackComponent) return FallbackComponent
      return null
    }
    return children
  }
}

export default ErrorBoundary
